/* eslint-disable react/button-has-type */
/* eslint-disable linebreak-style */
/* eslint-disable react/jsx-filename-extension */
/* eslint-disable import/extensions */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Button from "../elements/Button";
import first from "../assets/images/hero/1.webp";
import second from "../assets/images/hero/2.webp";
import third from "../assets/images/hero/3.webp";
import fourth from "../assets/images/hero/4.webp";

export default function Hero() {
  const slides = [
    {
      id: 1,
      title: "Governance. Integrity. Trust.",
      text: "Connecting independent directors and forward-thinking companies to build stronger, transparent, and future-ready boards.",
      image: fourth,
      gradient: "from-blue-50 via-white to-white",
      align: "right",
    },
    {
      id: 2,
      title: "Empowering Companies",
      text: "Find the right independent directors to elevate governance standards and future-proof your business.",
      image: second,
      gradient: "from-purple-50 via-white to-white",
      align: "left",
    },
    {
      id: 3,
      title: "For Independent Directors",
      text: "Grow your career, join impactful boards, and shape corporate decision-making that matters.",
      image: third,
      gradient: "from-pink-50 via-white to-white",
      align: "right",
    },
    {
      id: 4,
      title: "Build Better Boards",
      text: "Let's collaborate to bring transparency and trust to corporate governance.",
      image: first,
      gradient: "from-green-50 via-white to-white",
      align: "left",
    },
  ];

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <div className="relative w-full h-screen overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.section
          key={slides[current].id}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className="absolute inset-0 w-full h-full bg-cover bg-center flex items-center justify-center"
          style={{
            backgroundImage: `linear-gradient(to bottom right, rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url(${slides[current].image})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >

          {/* Decorative Background Shapes */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.1, scale: 1 }}
            transition={{ duration: 1 }}
            className="absolute -top-20 -left-20 w-[26rem] h-[26rem] bg-theme-purple rounded-full blur-3xl"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.1, scale: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="absolute bottom-0 right-0 w-[28rem] h-[28rem] bg-theme-blue rounded-full blur-3xl"
          />

          <div
            className="container mx-auto px-6 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            {/* Alternate layout based on align */}
            {slides[current].align === "left" && (
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="flex justify-center order-2 lg:order-1"
              >
                <img
                  loading="lazy"
                  className="max-w-md lg:max-w-lg xl:max-w-xl drop-shadow-2xl hover:scale-105 transition-transform duration-500 rounded-2xl"
                  src={slides[current].image}
                  alt={slides[current].title}
                />
              </motion.div>
            )}

            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="flex flex-col items-center lg:items-start text-center lg:text-left bg-white/70 backdrop-blur-md rounded-3xl p-10 shadow-xl border border-gray-100"
            >
              <h1 className="text-3xl sm:text-4xl xl:text-5xl font-extrabold leading-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-theme-blue to-theme-purple">
                {slides[current].title}
              </h1>

              <p className="text-base text-gray-700 leading-relaxed mb-8 max-w-lg">
                {slides[current].text}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center lg:justify-start">

                <Button
                  href="/about-guvrn"
                  type="link"
                  className="inline-flex text-center items-center justify-center px-8 py-4 text-lg font-medium text-white bg-theme-purple rounded-full shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300"
                >
                  Learn More
                </Button>

                <Button
                  href="/contact-guvrn"
                  type="link"
                  className="inline-flex text-center items-center justify-center px-8 py-4 text-lg font-medium text-theme-purple border-2 border-theme-purple rounded-full hover:bg-theme-purple hover:text-white transition-all duration-300"
                >
                  Get in Touch
                </Button>
              </div>
            </motion.div>

            {slides[current].align === "right" && (
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="flex justify-center order-2"
              >
                <img
                  loading="lazy"
                  className="max-w-md lg:max-w-lg xl:max-w-xl drop-shadow-2xl hover:scale-105 transition-transform duration-500 rounded-2xl"
                  src={slides[current].image}
                  alt={slides[current].title}
                />
              </motion.div>
            )}
          </div>
        </motion.section>
      </AnimatePresence>

      {/* Navigation Dots */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
        {slides.map((_, index) => (
          <button
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              current === index ? "bg-theme-purple w-6" : "bg-gray-300"
            }`}
            onClick={() => setCurrent(index)}
          />
        ))}
      </div>

      {/* Clean Arrow Buttons */}
      <button
        onClick={() => setCurrent((prev) => (prev - 1 + slides.length) % slides.length)}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 shadow-lg transition"
      >
        <ArrowLeft className="w-6 h-6 text-theme-purple" />
      </button>

      <button
        onClick={() => setCurrent((prev) => (prev + 1) % slides.length)}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 shadow-lg transition"
      >
        <ArrowRight className="w-6 h-6 text-theme-purple" />
      </button>
    </div>
  );
}
